import { Product } from '@inter-ikea-storageone/range';
import React from 'react';
import Box from 'ui/Box';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import Price from 'ui/Price/Price';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import GetInStoreStockIndicator from '../StoreAvailability/StoreAvailability';
import ProductImage from 'components/ProductImage/ProductImage';
import GetHomeDeliveryStockIndicator from '../HomeDeliveryAvailability/HomeDeliveryAvailability';
import { getProductDescription } from 'products';
import Divider from 'ui/Divider/Divider';
import { useIntl } from 'react-intl';
import { useAvailability } from 'contexts/availability';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import ProductIdentifier from '@ingka/product-identifier';
import ComplianceInformation from './ComplianceInformation';
interface Props {
  products: Product[];
  isCombination: boolean;
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: ${({ theme }) => theme.space.xs} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[200]};
  padding-bottom: 1rem;
  width: 100%;
  :last-child {
    border: none;
  }
`;

const ProductImageWrapper = styled.div`
  width: 64px;
  height: 64px;
  min-width: 64px;
  margin-right: 1rem;
`;

const ProductInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const getItemsOrSizesString = (products: Product[], isCombination: boolean, formatMessage: any) => {
  const amount = products.length;
  if (isCombination) {
    return `${amount} ${formatMessage({
      id: 'storageone.items'
    })}`;
  }
  return `${amount} ${formatMessage({ id: 'storageone.sizes' })}`;
};

function VariantsTable({ products, isCombination }: Props) {
  const { formatMessage } = useIntl();
  const { storeId, zipCode } = useAvailability();

  const partsIds = products?.map(pp => pp.coreProductId);

  const counts: { [key: string]: number } = {};
  const uniqueProducts: Product[] = [];
  partsIds?.forEach(id => {
    const productForPart = products.find(p => p.coreProductId === id);

    const count = (counts[id] = (counts[id] || 0) + 1);
    if (count === 1) {
      productForPart && uniqueProducts.push(productForPart);
    }
  });

  return (
    <>
      {products.length > 1 && (
        <Body mb="md" mt="l" type={ThemeFontStyleTypeEnum.bodySmall} color="grey.500">
          {getItemsOrSizesString(products, isCombination, formatMessage)}
        </Body>
      )}
      <Divider />
      <Box>
        {uniqueProducts.map(product => {
          const productDescription = getProductDescription(product, {
            typeName: isCombination,
            designText: isCombination,
            communicatedSizeText: true
          });
          return (
            <Row key={product.coreProductId} data-testid={`row-${product.communicatedSizeText}`}>
              <ProductImageWrapper>
                <ProductImage
                  sizes="4rem"
                  image={product.images[0]}
                  alt={product.name + product.type}
                />
                {isCombination && (
                  <Title type={ThemeFontStyleTypeEnum.headingXSmall}>
                    {counts[product.coreProductId]}x
                  </Title>
                )}
              </ProductImageWrapper>
              <ProductInfoWrapper>
                <Title type={ThemeFontStyleTypeEnum.headingXSmall}>{product.name}</Title>
                <Body type={ThemeFontStyleTypeEnum.bodyMedium} color="grey.500">
                  {productDescription}
                </Body>
                <Title type={ThemeFontStyleTypeEnum.headingXSmall}>
                  <Price amount={product.priceInformation.currentPrice} />
                </Title>
                {product.ruItemNo && (
                  <ProductIdentifier value={ProductUtility.formatItemNo(product.ruItemNo)} />
                )}
                {storeId && (
                  <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
                    <GetInStoreStockIndicator product={product} bigIcon />
                  </Body>
                )}
                {zipCode && (
                  <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
                    <GetHomeDeliveryStockIndicator product={product} zipCode={zipCode} bigIcon />
                  </Body>
                )}
                <ComplianceInformation product={product} />
              </ProductInfoWrapper>
            </Row>
          );
        })}
      </Box>
    </>
  );
}

export default VariantsTable;
